import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { default as dayjs } from "dayjs";
import {
  GridColDef,
  GridActionsCellItem,
  GridRenderCellParams,
  GridPaginationModel,
} from "@mui/x-data-grid";
import {
  DeleteConfirmModal,
  ProductGroupModal,
  Table,
} from "../../molecules";
import { Loading, StatusBarUser } from "../../atoms";
import {
  useAuthorization,
  useGetProductGroups,
  useDeleteProductGroup,
  ProductGroup,
  useUpdateProductGroup,
} from "../../../hooks";
import { useNavigate, useLocation } from "react-router-dom";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { useQueryClient } from "react-query";
import { downloadExcel } from "../../../util/downloadExcel";
import { useUserInfo } from "../../../store/User.state";

function RenderStatus(props: GridRenderCellParams<any, string>) {
  const { value } = props;
  return <>{value && <StatusBarUser status={value} />}</>;
}

type UpdateProductGroupState = {
  productGroup: ProductGroup | null;
  openState: boolean;
};

export const ProductGroupsList = ({
  refetchTrigger,
}: {
  refetchTrigger: boolean;
}) => {

  const theme = useTheme();
  const [userInfo] = useUserInfo();
  const { showDelete, showUpdate } = useAuthorization();
  const location = useLocation();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });

  const [tab, setTab] = useState<"active" | "passive">("active");

  const {
    data: activeGroups,
    isLoading: isActiveLoading,
  } = useGetProductGroups({
    status: "ACTIVE",
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
  });

  const {
    data: passiveGroups,
    isLoading: isPassiveLoading,
  } = useGetProductGroups({
    status: "PASSIVE",
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
  });

  const { mutate: deleteProductGroup, isLoading: isDeleteLoading } =
    useDeleteProductGroup();
  const { mutate: updateProductGroup, isLoading: isUpdateLoading } = useUpdateProductGroup();

  const setSnackbar = useSetSnackBar();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const [updateProductGroupState, setUpdateProductGroupState] =
    useState<UpdateProductGroupState | null>(null);
  const [exportTrigger, setExportTrigger] = useState<{
    status: string;
    page: number;
    size: number;
  } | null>(null);

  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [selectedProductGroupForStatusChange, setSelectedProductGroupForStatusChange] = useState<ProductGroup | null>(null);
  const [targetStatus, setTargetStatus] = useState<'ACTIVE' | 'PASSIVE' | null>(null);


  const currentData =
    tab === "active" ? activeGroups?.data : passiveGroups?.data;

  const hasLoading =
    isActiveLoading ||
    isPassiveLoading ||
    isDeleteLoading ||
    isUpdateLoading;

  const editRow = React.useCallback(
    (productGroup: ProductGroup) => () => {
      setUpdateProductGroupState({
        productGroup: productGroup,
        openState: true,
      });
    },
    []
  );

  const deleteRow = React.useCallback(
    (productGroup: ProductGroup) => () => {
      setIsDeleteModalOpen(true);
      setSelectedRowId(Number(productGroup.id));
    },
    []
  );

  const handleStatusChange = React.useCallback(
    (productGroup: ProductGroup, status: 'ACTIVE' | 'PASSIVE') => () => {
      setIsUpdateStatusModalOpen(true);
      setSelectedProductGroupForStatusChange(productGroup);
      setTargetStatus(status);
    },
    []
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries(["product", "groups"]);
  }, [refetchTrigger]);

  const handleChangeStatusConfirm = () => {
    if (!selectedProductGroupForStatusChange || !targetStatus) return;

    const request: ProductGroup = {
      ...selectedProductGroupForStatusChange,
      status: targetStatus
    };

    updateProductGroup(request, {
      onSuccess: (data: any) => {
        if (data.isSuccess) {
          queryClient.invalidateQueries(["product", "groups"]);
          setSnackbar({
            severity: "success",
            isOpen: true,
            description: data.message,
          });
        }
        else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });

    setIsUpdateStatusModalOpen(false);
    setSelectedProductGroupForStatusChange(null);
    setTargetStatus(null);
  };

  const handleDeleteConfirm = () => {
    if (!selectedRowId) {
      setSnackbar({
        severity: "error",
        isOpen: true,
        description: "Silinecek ürün grubu seçilmemiş",
      });
      return;
    }
    deleteProductGroup(
      { id: selectedRowId, status: 'DELETED' },
      {
        onSuccess: (data: any) => {
          if (data?.isSuccess) {
            setSnackbar({
              severity: "success",
              isOpen: true,
              description: data.message || "Ürün grubu başarıyla silindi",
            });
            queryClient.invalidateQueries(["product", "groups"]);
          } else {
            setSnackbar({
              severity: "error",
              description: data?.message || "Silme sırasında hata oluştu",
              isOpen: true,
            });
          }
        },
      }
    );
    setIsDeleteModalOpen(false);
  };

  const onSave = () => {
    const totalItems =
      tab === "active"
        ? activeGroups?.data?.totalItems
        : passiveGroups?.data?.totalItems;

    setExportTrigger({
      status: tab.toUpperCase() as "ACTIVE" | "PASSIVE",
      page: 1,
      size: totalItems || 999,
    });
  };

  const columns: GridColDef<ProductGroup, any, any>[] = useMemo(() => {
    return [
      {
        field: "Aksiyonlar",
        type: "actions",
        width: 80,
        getActions: (params) => {
          const actions = [];
          if (tab === 'active') {
            if (showUpdate) {
              actions.push(
                <GridActionsCellItem
                  label="Düzenle"
                  onClick={editRow(params.row)}
                  showInMenu
                />
              );
            }
            actions.push(
              <GridActionsCellItem
                label="Pasife Al"
                onClick={handleStatusChange(params.row, 'PASSIVE')}
                showInMenu
              />
            );
            if (showDelete) {
              actions.push(
                <GridActionsCellItem
                  label="Sil"
                  onClick={deleteRow(params.row)}
                  showInMenu
                />
              );
            }
          } else { // passive tab
            actions.push(
              <GridActionsCellItem
                label="Aktif Et"
                onClick={handleStatusChange(params.row, 'ACTIVE')}
                showInMenu
              />
            );
            if (showDelete) {
              actions.push(
                <GridActionsCellItem
                  label="Sil"
                  onClick={deleteRow(params.row)}
                  showInMenu
                />
              );
            }
          }
          return actions;
        },
      },
      { field: "id", headerName: "ID", width: 100 },
      { field: "name", headerName: "Grup Adı", width: 250, flex: 1 },
      { field: "merchantId", headerName: "Merchant ID", width: 130 },
      {
        renderCell: RenderStatus,
        field: "status",
        headerName: "Durum",
        width: 100,
      },
      {
        field: "createDate",
        headerName: "Oluşturma T.",
        width: 200,
        renderCell: (params) =>
          dayjs(params.value).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        field: "updateDate",
        headerName: "Güncelleme T.",
        width: 200,
        renderCell: (params) =>
          params.value
            ? dayjs(params.value).format("YYYY-MM-DD HH:mm:ss")
            : "-",
      },
    ];
  }, [deleteRow, editRow, showDelete, showUpdate, tab, handleStatusChange]);

  const Exporter = ({ params, onComplete }: { params: object; onComplete: () => void }) => {
    const { data: exportData, isSuccess, isError } = useGetProductGroups(params);
    const setSnackbar = useSetSnackBar();
  
    useEffect(() => {
      if (isSuccess && exportData) {
        const columns = {
          id: "ID",
          name: "Grup Adı",
          merchantId: "Merchant ID",
          status: "Durum",
          createDate: "Oluşturma T.",
          updateDate: "Güncelleme T.",
        };
  
        const fieldValue = (data: ProductGroup[] | undefined) => {
          return data?.map((item) => ({
            id: item.id,
            name: item.name,
            merchantId: item.merchantId,
            status: item.status,
            createDate: dayjs(item.createDate).format("YYYY-MM-DD HH:mm:ss"),
            updateDate: item.updateDate
              ? dayjs(item.updateDate).format("YYYY-MM-DD HH:mm:ss")
              : "-",
          }));
        };
  
        const refactoredData = fieldValue(exportData?.data?.result);
  
        if (refactoredData && refactoredData.length > 0) {
          downloadExcel(refactoredData, "Ürün Grupları", columns);
        } else {
          setSnackbar({
            severity: "info",
            isOpen: true,
            description: "Dışa aktarılacak veri bulunamadı.",
          });
        }
        onComplete();
      } else if (isError) {
        setSnackbar({
          severity: "error",
          description: "Veri indirilirken bir hata oluştu.",
          isOpen: true,
        });
        onComplete();
      }
    }, [isSuccess, isError, exportData, onComplete, setSnackbar]);
  
    return null;
  };

  const handleExportComplete = React.useCallback(() => {
    setExportTrigger(null);
  }, []);

  return (
    <>
      {hasLoading && <Loading />}
      <Stack flex={1} p={2}>
        <Tabs
          value={tab}
          onChange={(e, v) => {
            setTab(v);
          }}
          sx={{ marginBottom: 2 }}
        >
          <Tab label="Aktif Ürün Grupları" value="active" />
          <Tab label="Pasif Ürün Grupları" value="passive" />
        </Tabs>

        <Table
          columnVisibilityConfigKey="productGroupColumnVisibilityKey"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={currentData?.totalItems || 0}
          sx={{ width: isDesktop ? 1308 : window.innerWidth - 50 }}
          isRowSelectable={() => false}
          rows={currentData?.result || []}
          columns={columns}
          exportFileName="Ürün Grupları"
          onSave={() => onSave()}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Silme Onayı"
          content="Seçili ürün grubunu silmek istediğinize emin misiniz?"
        />

        <DeleteConfirmModal
            isOpen={isUpdateStatusModalOpen}
            onClose={() => setIsUpdateStatusModalOpen(false)}
            onConfirm={handleChangeStatusConfirm}
            title={`${targetStatus === 'ACTIVE' ? 'Aktif' : 'Pasif'} Etme Onayı`}
            content={`Seçili ürün grubunu ${targetStatus === 'ACTIVE' ? 'aktif' : 'pasif'} etmek istiyor musunuz?`}
            confirmButtonText={`${targetStatus === 'ACTIVE' ? 'Aktif Et' : 'Pasif Et'}`}
            closeButtonText="Vazgeç"
        />

        <ProductGroupModal
          mode="edit"
          productGroupId={updateProductGroupState?.productGroup?.id}
          onClose={() => {
            setUpdateProductGroupState(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries(["product", "groups"]);
          }}
          open={updateProductGroupState?.openState ?? false}
        />
        {exportTrigger && <Exporter params={exportTrigger} onComplete={handleExportComplete} />}
      </Stack>
    </>
  );
};